import { X } from "lucide-react";
import { type JSX, useState } from "react";

import { Button } from "@/components/ui/button";

interface RefineKnowledgeBannerProps {
  /** Item id being refined; surfaced for debugging via title. */
  itemId: string;
  /** The fact's current text — the operator's reference while writing
   * the refinement instructions. */
  currentText: string;
  onExit: () => void;
}

const PREVIEW_CHARS = 300;

export function RefineKnowledgeBanner({
  itemId,
  currentText,
  onExit,
}: RefineKnowledgeBannerProps): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const needsTruncation = currentText.length > PREVIEW_CHARS;
  const visibleText =
    needsTruncation && !expanded
      ? `${currentText.slice(0, PREVIEW_CHARS).trimEnd()}…`
      : currentText;

  return (
    <div
      className="mb-2 rounded-md border border-brand-extra-2-40/40 bg-brand-extra-2-40/5 px-3 py-2"
      data-testid="refine-knowledge-banner"
      title={`refinando ${itemId}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-label-2 uppercase tracking-wider text-brand-extra-2-40">
            refinando este conocimiento
          </p>
          <p className="mt-1 whitespace-pre-wrap break-words text-body-3 text-foreground">
            {visibleText}
          </p>
          {needsTruncation ? (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-1 text-label-2 text-brand-extra-2-40 underline-offset-2 hover:underline"
              data-testid="refine-knowledge-banner-toggle"
            >
              {expanded ? "ver menos" : "ver completo"}
            </button>
          ) : null}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onExit}
          aria-label="Salir del modo refinar"
          title="Salir del modo refinar"
          data-testid="refine-knowledge-banner-exit"
        >
          <X aria-hidden className="size-4" />
        </Button>
      </div>
    </div>
  );
}
